import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'src/ui/library/styled';
import TextField from '@material-ui/core/TextField';
import { List } from 'react-virtualized';
import ArrowDropDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowDropUpIcon from '@material-ui/icons/KeyboardArrowUp';

const styles = (theme, props) => ({
  textField: {
    height: '48px',
    width: '100%',
    maxWidth: '300px',
    color: theme.colors.eyConfidentGrey,
    backgroundColor: theme.colors.eyGrey5,
    border: '1px solid transparent',
    overflow: 'hidden',
  },
  textFieldFocus: {
    border: `1px solid ${theme.colors.eyGrey6}`,
  },
  menu: {
    maxHeight: '260px',
    left: '-8px',
    top: '1px',
    //maxWidth: '300px',
    //width: '100px',
  },
  menuPaper: {
    overflowX: props.virtualized ? 'hidden' : 'hidden',
    overflowY: props.virtualized ? 'hidden' : 'auto',
    border: '1px solid #C4C4CE',
  },
  select: {
    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  selectEmpty: {
    marginTop: theme.spacing.unit * 2,
    width: 100,
  },
  selectIcon: {
    color: theme.colors.eyConfidentGrey,
    top: 'calc(50% - 19px)',
    right: '4px',
  },
  inputRoot: {
    paddingLeft: '7px',
    marginTop: '8px',
  },
  formLabel: {
    color: theme.colors.eyConfidentGrey,
    paddingLeft: '8px',
    '& span': {
      color: theme.colors.red,
    },
  },
  formControl: {
    transform: 'translate(0, 16px) scale(1)',
    paddingLeft: '10px',
  },
  shrink: {
    transform: 'translate(0, 8px) scale(0.75)',
    marginBottom: '8px',
    color: theme.colors.eyGrey1,
    width: 'calc(100% + 100% * 0.20)', // due to the shrink transform the width needs to be accounted for
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  focused: {
    '&$focused': {
      color: theme.colors.eyGrey1,
    },
  },
});

class DropdownBase extends React.Component {
  static propTypes = {
    /** Classname to be applied to <TextField> */
    className: PropTypes.string,
    /** If `true`, the input will not have an underline. */
    disableUnderline: PropTypes.bool,
    /** If `true`, textfield will be in error state */
    hasError: PropTypes.bool,
    /** If `true`, Dropdown will render virtulized */
    virtualized: PropTypes.bool,
    /** Used in conjunction with `virtualized` allowing a user to render proper label */
    listOptions: PropTypes.array,
    /** Override or extend default styles */
    styles: PropTypes.object,
    /** Name attribute of the `input` element. */
    name: PropTypes.string.isRequired,
    /** Value of `input` element */
    value: PropTypes.any,
    /** Text used for input label */
    label: PropTypes.string,
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
  };

  static defaultProps = {
    virtualized: false,
  };

  state = {
    focused: false,
    menuOpen: false,
    index: null,
  };

  handleOnFocus = status => {
    this.setState({ focused: status });
  };

  handleMenuOpen = status => {
    this.setState({ menuOpen: status });
  };

  getVirtualizedValue = selectedIndex => {
    const { listOptions } = this.props;

    if (!listOptions) return selectedIndex;

    let displayVal = selectedIndex;

    listOptions.forEach(item => {
      if (item.lookUpBindValue === selectedIndex) displayVal = item.displayText;
    });

    return displayVal;
  };

  renderSingleRow = row => {
    const { key, index, style } = row;
    const { children } = this.props;

    return React.cloneElement(children[index], {
      key: key,
      style: {
        ...style,
        paddingTop: '0',
        paddingBottom: '0',
      },
      onClick: () => {
        this.props.onChange({
          target: {
            value: children[index].props.value,
          },
        });
        this.setState({ menuOpen: false });
      },
    });
  };

  render() {
    const {
      classes,
      id,
      name,
      className,
      disableUnderline,
      hasError,
      onChange,
      value,
      children,
      virtualized,
      listOptions,
      ...otherProps
    } = this.props;

    const textClassName = classNames(
      {
        [classes.textField]: true,
        [classes.textFieldFocus]: this.state.focused,
        [classes.hasError]: hasError,
      },
      className,
    );

    return (
      <TextField
        id={id}
        name={name}
        value={value}
        className={textClassName}
        select
        SelectProps={{
          native: false,
          open: this.state.menuOpen,
          onFocus: this.handleOnFocus.bind(null, true),
          onBlur: this.handleOnFocus.bind(null, false),
          onOpen: this.handleMenuOpen.bind(null, true),
          onClose: this.handleMenuOpen.bind(null, false),
          IconComponent: this.state.menuOpen === true ? ArrowDropUpIcon : ArrowDropDownIcon,
          renderValue: virtualized ? selected => this.getVirtualizedValue(selected) : undefined,
          classes: {
            select: classes.select,
            icon: classes.selectIcon,
          },
          MenuProps: {
            className: classes.menu,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            classes: {
              paper: classes.menuPaper,
            },
            MenuListProps: {
              disablePadding: virtualized,
            },
            getContentAnchorEl: null,
          },
        }}
        InputLabelProps={{
          classes: {
            formControl: classes.formControl,
            shrink: classes.shrink,
          },
          FormLabelClasses: {
            root: classes.formLabel,
            focused: classes.focused,
          },
        }}
        InputProps={{
          disableUnderline,
          className: classes.inputRoot,
        }}
        onChange={!virtualized ? e => onChange(e, children) : undefined}
        {...otherProps}>
        {virtualized ? (
          <List width={299} height={160} rowCount={children.length} rowHeight={48} rowRenderer={this.renderSingleRow} />
        ) : (
          children
        )}
      </TextField>
    );
  }
}

export { DropdownBase };
export default styled(DropdownBase)(styles);
